import { getOddsFormatIs, type OddsFormat } from '~/vendor/cx-web/domain-types';
import {
  convertDecimalOddsToFractional,
  convertDecimalOddsToProbability,
  convertDecimalToAmerican,
  isPredictionMarket,
} from '~/vendor/cx-web/feature-odds-format';

import { type Bet, betslipType, type BookieOdds, type Odds } from '../types';

// NOTE: this is very similar to getBestOdds
export const getOdds = (odds: any, opening = false, userOddsFormat: OddsFormat): string => {
  const oddsFormatIs = getOddsFormatIs(userOddsFormat);

  let betOdds;
  switch (true) {
    case oddsFormatIs.American:
      if (isPredictionMarket(odds.bookmakerCode)) {
        const decimal = opening ? (odds.openingOddsDecimal ?? odds.oddsDecimal) : odds.oddsDecimal;
        // decimal <= 1 isn't a real price (a deselected acca leg is 1) — fall through to the '--' fallback.
        betOdds = decimal > 1 ? convertDecimalOddsToProbability(decimal) : undefined;
        break;
      }
      betOdds = opening ? odds.openingOddsUS : odds.oddsUs;
      if (betOdds > 0 && !betOdds.includes('+')) {
        betOdds = `+${betOdds}`;
      }
      break;
    case oddsFormatIs.Decimal:
      // no idea wdf betOdds is reassigned twice here
      betOdds = opening ? odds.openingOddsDecimal : odds.oddsDecimal;
      betOdds = betOdds ? betOdds.toFixed(2) : '--';
      break;
    case oddsFormatIs.Fractional:
      betOdds = opening ? odds.openingOddsFractional : odds.oddsFractional;
      break;
  }
  return betOdds || '--';
};

export const getSelectedBookieBetOdds = (selectedBookie: BookieOdds, userOddsFormat: OddsFormat): string | number => {
  const selectedBookieOddsFormat = (() => {
    switch (userOddsFormat) {
      case 'fractional':
        return 'oddsFractional';
      case 'decimal':
        return 'oddsDecimal';
      case 'american':
        return 'oddsUs';
      case 'traditional':
        return 'oddsFractional';
    }
  })();

  return selectedBookie[selectedBookieOddsFormat];
};

export const getBestOdds = (bet: any, userOddsFormat: OddsFormat): string => {
  const oddsFormatIs = getOddsFormatIs(userOddsFormat);

  let betOdds;
  switch (true) {
    case oddsFormatIs.American:
      betOdds = bet.bestOddsUs || bet.oddsUs;
      if (betOdds > 0) {
        betOdds = `+${betOdds}`;
      }
      break;
    case oddsFormatIs.Decimal:
      betOdds = bet.bestOddsDecimal || bet.bestOpeningOddsDecimal;
      break;
    case oddsFormatIs.Fractional:
      betOdds = bet.bestOddsFractional || bet.oddsFractional;
      break;
  }

  return betOdds || '--';
};

export const getBestOddsFromBet = (odds: Odds[]) => {
  if (!odds?.length) {
    return {};
  }

  const bestOdds = odds.reduce((prev, current) => (prev.oddsDecimal > current.oddsDecimal ? prev : current));

  return {
    bestOddsDecimal: bestOdds.oddsDecimal,
    bestOddsUs: Number(bestOdds.oddsUs),
    bestOddsFractional: bestOdds.oddsFractional,
    bestBookie: bestOdds.bookmakerCode,
  };
};

export const calculateBookieOdds = (data: Bet[]): BookieOdds[] => {
  const type = data.length === 1 ? betslipType.SINGLE : betslipType.MULTIPLE;
  const accumulatedOdds: BookieOdds[] = [];

  let betId: number;
  let bookie: string;
  let bookmakerType: 'traditional' | 'exchange';
  let oddsDec: number;
  let oddsFrac: string;
  let oddsUs: string;
  let bookieIndex: number;

  let i: number;
  let j: number;
  let isSelected: boolean;

  const bets = data.map(bet => {
    return {
      ...bet,
      ...getBestOddsFromBet(bet.odds),
    };
  });

  // Loop through each bet and extract betId.
  for (i = 0; i < bets.length; i++) {
    if (type === betslipType.SINGLE || (bets[i].uiOptions && type === betslipType.MULTIPLE)) {
      betId = bets[i].betId;

      // Loop through odds for each bet and extract bookie and bookie odds.
      for (j = 0; j < bets[i].odds.length; j++) {
        bookie = bets[i].odds[j].bookmakerCode;
        bookmakerType = bets[i].odds[j].bookmakerType;

        isSelected = bets[i].uiOptions.selected;
        // Dont include the bet in calculations if not selected.
        if (isSelected) {
          oddsDec = bets[i].odds[j].oddsDecimal;
          oddsFrac = bets[i].odds[j].oddsFractional;
          oddsUs = bets[i].odds[j].oddsUs;
        } else {
          oddsDec = 1;
          oddsFrac = '';
          oddsUs = '';
        }

        // Loop through current array of accumulated odds and see
        // if bookiemaker is already in the array with some odds.
        bookieIndex = accumulatedOdds
          .map(item => (typeof item.bookmakerCode !== 'undefined' ? item.bookmakerCode : ''))
          .indexOf(bookie);

        // If the bookie was in the array increment their odds.
        if (bookieIndex > -1) {
          accumulatedOdds[bookieIndex].oddsDecimal *= oddsDec;
          accumulatedOdds[bookieIndex].oddsUs = convertDecimalToAmerican(accumulatedOdds[bookieIndex].oddsDecimal);
          accumulatedOdds[bookieIndex].oddsFractional = convertDecimalOddsToFractional(
            accumulatedOdds[bookieIndex].oddsDecimal,
          );
          if (isSelected) {
            accumulatedOdds[bookieIndex].betIds?.push(betId);
          }

          // If the bookie wasn't found in the array then add an entry for them.
        } else {
          const tempAccaOdds: BookieOdds = {
            bookmakerCode: bookie,
            oddsDecimal: oddsDec,
            oddsFractional: oddsFrac,
            oddsUs,
            betIds: [],
            bookmakerType,
          };
          if (isSelected) {
            tempAccaOdds.betIds?.push(betId);
          }
          accumulatedOdds.push(tempAccaOdds);
        }
      }
    }
  }

  // Sort the acca entries based on odds in descending order.
  return accumulatedOdds.sort((a, b) => b.oddsDecimal - a.oddsDecimal);
};

export const calculateSelectedBookieOdds = (data: Bet[], selectedBookieCode?: string): BookieOdds | undefined => {
  const bookieOdds = calculateBookieOdds(data);

  const singleBetslip = data.length === 1;
  const singleBookieOdds = bookieOdds.length === 1;
  const multiBookieOdds = bookieOdds.length > 1;

  switch (true) {
    case singleBetslip || singleBookieOdds: {
      return bookieOdds[0];
    }
    case multiBookieOdds: {
      const isExchangeBookie = bookieOdds[0]?.bookmakerType === 'exchange';
      const bookiePosition = isExchangeBookie ? 1 : 0;

      return (
        bookieOdds.find(o => o.bookmakerCode === selectedBookieCode) || bookieOdds[bookiePosition] || bookieOdds[0]
      );
    }
  }
};

export const calculateBookieOddsAndSelectedBookieOdds = (
  data: Bet[],
  selectedBookieCode?: string,
): {
  bookieOdds: BookieOdds[];
  selectedBookie: BookieOdds | undefined;
} => {
  return {
    bookieOdds: calculateBookieOdds(data),
    selectedBookie: calculateSelectedBookieOdds(data, selectedBookieCode),
  };
};
